import { ApplicationClientIds, PlanungModules } from '@bp/pim-auth-constants';
import { Button, Grid, GridColumn, GridRow, Switch } from '@bp/ui-components';
import dayjs from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CombinedError, useQuery } from 'urql';
import {
  PimListOrganizationsDocument,
  PimListOrganizationsQuery,
  PimListOrganizationsQueryVariables,
  usePimUpdateApplicationMutation,
} from '../../../client/graphql-client-defs';
import styles from './OrganizationPlanungForm.module.scss';

interface OrganisationPlanungFormProps {
  uuid?: string;
  afterSubmit: () => void;
}

interface OrganisationPlanungData {
  uuid: string;
  planungEnable: boolean;
  validFrom?: dayjs.Dayjs;
  validUntil?: dayjs.Dayjs;
  teachingLoadEnabled: boolean;
  timetableEnabled: boolean;
  solverEnabled: boolean;
  epochenEnabled: boolean;
  substitutionEnabled: boolean;
  appEnabled: boolean;
}

export const OrganizationPlanungForm = ({ uuid, afterSubmit }: OrganisationPlanungFormProps) => {
  const { t } = useTranslation();
  const [, updateApplication] = usePimUpdateApplicationMutation();
  const [{ data, fetching }] = useQuery<PimListOrganizationsQuery, PimListOrganizationsQueryVariables>({
    query: PimListOrganizationsDocument,
    variables: {
      where: {
        uuid: uuid,
      },
      options: {
        limit: 1,
      },
    },
    pause: !uuid,
  });

  const handleSubmit = async (values: OrganisationPlanungData, formHelpers: FormikHelpers<OrganisationPlanungData>) => {
    if (values) {
      const { uuid, planungEnable, ...data } = values;

      const licensedModules: string[] = [];
      if (data.teachingLoadEnabled) {
        licensedModules.push(PlanungModules.teachingLoad);
      }
      if (data.timetableEnabled) {
        licensedModules.push(PlanungModules.timetable);
      }
      if (data.solverEnabled) {
        licensedModules.push(PlanungModules.solver);
      }
      if (data.epochenEnabled) {
        licensedModules.push(PlanungModules.teachingBlocks);
      }
      if (data.substitutionEnabled) {
        licensedModules.push(PlanungModules.substitution);
      }
      if (data.appEnabled) {
        licensedModules.push(PlanungModules.app);
      }

      let mutationError: CombinedError | undefined;

      if (!planungEnable) {
        const { error } = await updateApplication({
          where: { clientId: ApplicationClientIds.Planung },
          update: {
            licensedOrganizations: [
              {
                disconnect: [{ where: { node: { uuid: uuid } } }],
              },
            ],
            accessGrantedOrganizations: [
              {
                disconnect: [{ where: { node: { uuid: uuid } } }],
              },
            ],
          },
        });
        mutationError = error;
      } else {
        const { error } = await updateApplication({
          where: { clientId: ApplicationClientIds.Planung, licensedOrganizations_SINGLE: { uuid: uuid } },
          update: {
            licensedOrganizations: [
              {
                connect: [{ overwrite: true, where: { node: { uuid: uuid } } }],
                where: { node: { uuid: uuid } },
                update: {
                  edge: {
                    licensedModules: licensedModules,
                    notBefore: data.validFrom?.format('YYYY-MM-DD'),
                    notAfter: data.validUntil?.format('YYYY-MM-DD'),
                  },
                },
              },
            ],
            accessGrantedOrganizations: [
              {
                connect: [{ overwrite: true, where: { node: { uuid: uuid } } }],
              },
            ],
          },
        });
        mutationError = error;
      }

      if (!mutationError) {
        afterSubmit();
        formHelpers.resetForm();
      }
    }
  };

  const organization = data && data.organizations ? data.organizations[0] : undefined;
  const planungEdge = organization?.licensedApplicationsConnection.edges.find(
    (e) => e.node.clientId === ApplicationClientIds.Planung,
  );

  const initialValues: OrganisationPlanungData = {
    uuid: organization?.uuid ?? '',
    planungEnable: planungEdge !== undefined,
    teachingLoadEnabled: planungEdge?.properties.licensedModules?.includes(PlanungModules.teachingLoad) ?? false,
    timetableEnabled: planungEdge?.properties.licensedModules?.includes(PlanungModules.timetable) ?? false,
    solverEnabled: planungEdge?.properties.licensedModules?.includes(PlanungModules.solver) ?? false,
    epochenEnabled: planungEdge?.properties.licensedModules?.includes(PlanungModules.teachingBlocks) ?? false,
    substitutionEnabled: planungEdge?.properties.licensedModules?.includes(PlanungModules.substitution) ?? false,
    appEnabled: planungEdge?.properties.licensedModules?.includes(PlanungModules.app) ?? false,
  };
  return (
    <>
      <Formik onSubmit={handleSubmit} initialValues={initialValues}>
        {({ handleChange, setFieldValue, values, isSubmitting, errors }) => {
          return (
            <Form>
              <Grid>
                <GridRow spacingBottom='l' spacingTop='s'>
                  <GridColumn width={2}>
                    <div className={styles['headline-switch']}>{t('planung.enabled')}</div>
                  </GridColumn>
                  <GridColumn width={8}>
                    <Switch
                      name='planungEnable'
                      onChange={async (element) => {
                        await setFieldValue('planungEnable', element.target.checked);
                      }}
                      checked={values.planungEnable}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow spacingBottom='l' spacingTop='none'>
                  <GridColumn width={8}>&nbsp;</GridColumn>
                  <GridColumn width={8}>&nbsp;</GridColumn>
                </GridRow>
                <GridRow spacingBottom='s' spacingTop='s'>
                  <GridColumn width={2}>
                    <div className={styles['headline-switch']}>{t('planung.teachingload')}</div>
                  </GridColumn>
                  <GridColumn width={8}>
                    <Switch
                      name='teachingLoadEnabled'
                      onChange={async (element) => {
                        await setFieldValue('teachingLoadEnabled', element.target.checked);
                      }}
                      checked={values.teachingLoadEnabled}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow spacingBottom='s' spacingTop='s'>
                  <GridColumn width={2}>
                    <div className={styles['headline-switch']}>{t('planung.timetable')}</div>
                  </GridColumn>
                  <GridColumn width={4}>
                    <Switch
                      name='timetableEnabled'
                      onChange={async (element) => {
                        await setFieldValue('timetableEnabled', element.target.checked);
                      }}
                      checked={values.timetableEnabled}
                    />
                  </GridColumn>
                  <GridColumn width={2}>
                    <div className={styles['headline-switch']}>{t('planung.solver')}</div>
                  </GridColumn>
                  <GridColumn width={4}>
                    <Switch
                      name='solverEnabled'
                      onChange={async (element) => {
                        await setFieldValue('solverEnabled', element.target.checked);
                      }}
                      checked={values.solverEnabled}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow spacingBottom='s' spacingTop='s'>
                  <GridColumn width={2}>
                    <div className={styles['headline-switch']}>{t('planung.substitution')}</div>
                  </GridColumn>
                  <GridColumn width={4}>
                    <Switch
                      name='substitutionEnabled'
                      onChange={async (element) => {
                        await setFieldValue('substitutionEnabled', element.target.checked);
                      }}
                      checked={values.substitutionEnabled}
                    />
                  </GridColumn>
                  <GridColumn width={2}>
                    <div className={styles['headline-switch']}>{t('planung.app')}</div>
                  </GridColumn>
                  <GridColumn width={4}>
                    <Switch
                      name='appEnabled'
                      onChange={async (element) => {
                        await setFieldValue('appEnabled', element.target.checked);
                      }}
                      checked={values.appEnabled}
                    />
                  </GridColumn>
                </GridRow>
              </Grid>

              <div className={'modal-bottom'}>
                <Button
                  type='submit'
                  disabled={isSubmitting || fetching || (errors && Object.values(errors).length > 0)}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
